import url from 'url'

import Cookies from 'js-cookie'
import BluebirdPromise from 'bluebird'
import axios, { CancelToken } from 'axios'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import mapKeys from 'lodash/mapKeys'
import paramify from 'jquery-param'
import set from 'lodash/set'
import snakeCase from 'lodash/snakeCase'
import startsWith from 'lodash/startsWith'
import urljoin from 'url-join'

import { FICTIV_JWT_COOKIE } from '~/redux-modules/auth'
import { getenv } from '~/settings'
import defer from '~/lib/defer'

const methods = ['get', 'post', 'put', 'patch', 'delete']

const EVENT_ENDPOINT = '/api/v1/event'

function snakeCaseObject(obj) {
    return mapKeys(obj, (v, k) => snakeCase(k))
}

/*
 * This silly underscore is here to avoid a mysterious 'ReferenceError: ApiClient is not defined' error.
 * See Issue #14. https://github.com/erikras/react-redux-universal-hot-example/issues/14
 *
 * Remove it at your own risk.
 */
class _ApiClient {
    get csrf() {
        return get(document.getElementsByName('csrf-token'), '[0].content')
    }

    _errorCallbacks = []

    constructor({ adapter = axios } = {}) {
        this._axios = adapter

        const base = getenv('FICTIV_API_BASE')

        methods.forEach(method => {
            this[method] = (
                path,
                { params, data, type, passCredentials = true, config } = {},
            ) => {
                const { protocol } = url.parse(path)
                const cancelSource = CancelToken.source()
                const requestConfig = {
                    headers: { Accept: 'application/json' },
                    ...config,
                    paramsSerializer: paramify,
                    method,
                    cancelToken: cancelSource.token,
                    url: protocol ? path : urljoin(base, path),
                    data,
                }
                const isExternalRequest = !startsWith(requestConfig.url, base)
                const jwtToken = Cookies.get(FICTIV_JWT_COOKIE)

                if (jwtToken) {
                    set(requestConfig, 'withCredentials', true)

                    set(
                        requestConfig,
                        'headers.Authorization',
                        `Bearer ${jwtToken}`,
                    )
                }
                if (!isExternalRequest && passCredentials) {
                    set(requestConfig, 'withCredentials', true)
                    set(requestConfig, 'headers.X-CSRF-Token', this.csrf || '')
                }

                if (params) {
                    set(requestConfig, 'params', params)
                }

                if (type) {
                    set(requestConfig, 'headers.Content-Type', type)
                }

                const requestPromise = Promise.resolve(
                    this._axios.request(requestConfig),
                )
                    .then(({ data: response }) => response)
                    .catch(error => {
                        if (error.response) {
                            error.message = `${error.message} | ${error.response.status}`
                            const errorResponseMessage = get(
                                error,
                                'response.data.message',
                            )

                            if (errorResponseMessage) {
                                error.message += ` | ${errorResponseMessage}`
                            }

                            // Preserve compatibility between the old way that superagent
                            // shoved error response data on the error object
                            error.body = error.response.data
                        }
                        throw error
                    })
                    .catch(error => {
                        if (!isEmpty(this._errorCallbacks)) {
                            return BluebirdPromise.mapSeries(
                                this._errorCallbacks,
                                cb => {
                                    return cb(error)
                                },
                            ).then(() => {
                                cancelSource.cancel(
                                    'Operation cancel due to error being handled.',
                                )
                                return requestPromise.cancel()
                            })
                        }
                        throw error
                    })

                return requestPromise
            }
        })
    }

    catchError(cb) {
        this._errorCallbacks.push(cb)
    }

    // Direct call methods
    trackEvent(event, properties, _context) {
        if (!this.__track) {
            const { resolve, promise } = defer()
            const wait = 10
            const track = {
                promise,
                spool: [],
                call: debounce(() => {
                    resolve(track.spool)
                    this.post(EVENT_ENDPOINT, {
                        data: { events: track.spool },
                        passCredentials: false,
                    })
                    this.__track = null
                }, wait),
            }

            this.__track = track
        }

        const payload = {
            event: snakeCase(event),
            properties: snakeCaseObject(properties),
            context: snakeCaseObject(_context),
        }

        this.__track.spool.push(payload)
        this.__track.call()
        return this.__track.promise
    }
}

const ApiClient = _ApiClient

export default ApiClient
